"use client";
import { KeyboardEvent, memo } from "react";
import { useTranslations } from "@zapier/i18n";
import { FolderTreeItem, getMemberOwnerTooltipContent } from "@zapier/client";
import { Icon } from "@zapier/zinnia-icons";
import { Avatar, Text, Tooltip } from "@zapier/design-system";
import { DEFAULT_FOLDER_ID, PLACEHOLDER_PROFILE_ICON } from "@zapier/utils";
import { Focusable } from "react-aria-components";

import styles from "./FolderTreeBranch.module.css";
import { HighlightedText } from "../../common/Table";
import { NewFolderRow } from "./NewFolderRow";
import type {
  DestinationDisableControls,
  NewFolderControls,
} from "./FolderTree";

const FolderTreeNode = ({
  folder,
  folderLevel,
  isFolderExpanded,
  onExpand,
  onItemClick,
  searchQuery,
  selectedFolderId,
  disabled,
  tabIndex,
  onKeyDown,
  ariaOwns,
  disabledTooltip,
  showDelete,
  onDelete,
  deleteAriaLabel,
  isDeleting,
}: FolderTreeNodeProps) => {
  const t = useTranslations("folder-tree");
  const owner = folder.owner;
  const isSelected = selectedFolderId === folder.id;
  const isInteractionDisabled = !!disabled;
  const ownerName = getMemberOwnerTooltipContent(owner);
  // The virtual "<Full Name> (Personal)" row already carries its owner in the name.
  const isVirtualPersonalRow = folder.id === DEFAULT_FOLDER_ID;
  // VoiceOver announces neither aria-selected nor aria-disabled usefully on a
  // treeitem, so every piece of state has to reach the accessible name.
  const itemLabel = t("a11y.row-label", {
    name: folder.name,
    owner: ownerName && !isVirtualPersonalRow ? ownerName : "none",
    state: isInteractionDisabled
      ? "unavailable"
      : isSelected
        ? "selected"
        : "available",
    reason: isInteractionDisabled && disabledTooltip ? disabledTooltip : "none",
  });

  const row = (
    <div
      className={`${styles.folderTreeNode} ${isSelected ? styles.selectedFolderTreeNode : ""} ${isInteractionDisabled ? styles.disabledFolderTreeNode : ""}`}
      style={{
        paddingLeft: folderLevel * 24 + 8,
      }}
      role="treeitem"
      aria-label={itemLabel}
      // The group sits beside the row inside a role="none" li, so the browser
      // counts two nesting levels per tree level and announces children of a
      // root folder as level 3. State the real depth instead.
      aria-level={folderLevel + 1}
      aria-selected={isInteractionDisabled ? undefined : isSelected}
      aria-expanded={folder?.children?.length ? isFolderExpanded : undefined}
      aria-disabled={isInteractionDisabled}
      // Unavailable rows stay in the roving-tabindex order so their reason is reachable.
      tabIndex={tabIndex}
      onKeyDown={(event) => {
        if (
          isInteractionDisabled &&
          (event.key === "Enter" || event.key === " ")
        ) {
          event.preventDefault();
          return;
        }
        onKeyDown?.(event);
      }}
      aria-owns={ariaOwns}
      onClick={
        onItemClick && !isInteractionDisabled
          ? () => onItemClick(folder)
          : undefined
      }
    >
      {folder?.children?.length ? (
        <button
          type="button"
          className={`${styles.caretIcon} ${isFolderExpanded ? styles.caretOpen : styles.caretClosed}`}
          data-testid={`${folder.name}-${folder.id}-arrow-caretIcon`}
          // Excluded from Tab so the tree keeps one tab stop; expand/collapse is
          // driven from the row with ArrowRight/ArrowLeft.
          tabIndex={-1}
          aria-label={t("a11y.chevron-label", {
            isExpanded: String(isFolderExpanded),
            name: folder.name,
          })}
          aria-expanded={isFolderExpanded}
          aria-controls={ariaOwns}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onExpand?.();
          }}
          // The row's handler treats Enter and Space as "select this folder"
          // and calls preventDefault, which would swallow the button's own
          // activation. Keep those keys from reaching it.
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.stopPropagation();
            }
          }}
        >
          <Icon
            name="arrowSmallDown"
            size={18}
            color="GrayWarm9"
            aria-hidden={true}
          />
        </button>
      ) : (
        <span className={styles.chevronSpacer} aria-hidden />
      )}
      <span className={styles.folderMain}>
        <Icon
          aria-disabled={disabled}
          aria-hidden={true}
          name={folder.isShared ? "team" : "navFolder"}
          color={disabled ? "GrayWarm5" : "GrayWarm6"}
          className={styles.icon}
          size={18}
        />
        <span className={styles.folderName}>
          <Text
            type="Body2"
            textAlign="left"
            color={
              isSelected ? "TextLink" : disabled ? "TextWeaker" : undefined
            }
          >
            <HighlightedText text={folder.name} highlight={searchQuery} />
          </Text>
        </span>
      </span>
      <div className={styles.folderTrailing}>
        {isSelected ? (
          <span className={styles.folderCheck}>
            <Icon name="formCheck" size={20} aria-hidden={true} />
          </span>
        ) : undefined}
        {showDelete && onDelete && deleteAriaLabel ? (
          // Nested inside role="treeitem"; keep as a focusable role=button (not
          // IconButton) so we do not nest a real <button> in the tree row.
          <span
            className={styles.trashButton}
            role="button"
            tabIndex={0}
            aria-label={deleteAriaLabel}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              if (!isDeleting) onDelete(String(folder.id));
            }}
            onKeyDown={(event) => {
              if (event.key !== "Enter" && event.key !== " ") return;
              event.preventDefault();
              event.stopPropagation();
              if (!isDeleting) onDelete(String(folder.id));
            }}
          >
            <Icon name="actionTrash" size={16} />
          </span>
        ) : undefined}
        {owner ? (
          <div
            className={`${styles.folderOwnerAvatar}${disabled ? ` ${styles.folderOwnerAvatarDisabled}` : ""}`}
            aria-hidden="true"
            title={ownerName}
          >
            <Avatar
              name={owner.fullName}
              size="small"
              url={owner.photoUrl || PLACEHOLDER_PROFILE_ICON}
              isBlock
            />
          </div>
        ) : undefined}
      </div>
    </div>
  );

  if (disabled && disabledTooltip) {
    // Merge the tooltip trigger props onto `row` directly, since wrapping it
    // would add a role-less div[tabIndex] under ul[role="tree"].
    return (
      <Tooltip allowMultiline content={disabledTooltip}>
        <Focusable>{row}</Focusable>
      </Tooltip>
    );
  }

  return row;
};

interface FolderTreeBranchProps {
  folder: FolderTreeItem;
  folderLevel: number;
  onItemClick?: (folder: FolderTreeItem) => void;
  searchQuery?: string;
  selectedFolderId?: string;
  disabled?: boolean;
  expandedFolders?: Set<string>;
  onToggleExpanded?: (folderId: string | number) => void;
  focusableFolderId?: string;
  handleKeyDown?: (event: KeyboardEvent, folder: FolderTreeItem) => void;
  getFocusableTabIndex?: (folderId: string) => 0 | -1;
  isFirstItem?: boolean;
  newFolder?: NewFolderControls;
  destinationDisables?: DestinationDisableControls;
  sessionCreatedFolderIds?: Set<string>;
  onDeleteSessionFolder?: (folderId: string) => void;
  deleteSessionFolderAriaLabel?: string;
  isDeletingSessionFolder?: boolean;
}

interface FolderTreeNodeProps extends FolderTreeBranchProps {
  isFolderExpanded?: boolean;
  onExpand?: () => void;
  showDelete?: boolean;
  onDelete?: (folderId: string) => void;
  deleteAriaLabel?: string;
  isDeleting?: boolean;
  tabIndex?: number;
  onKeyDown?: (event: KeyboardEvent) => void;
  ariaOwns?: string;
  disabledTooltip?: string;
}

const FolderTreeBranchComponent = ({
  folder,
  searchQuery,
  folderLevel = 0,
  onItemClick,
  selectedFolderId,
  disabled,
  expandedFolders = new Set(),
  onToggleExpanded,
  focusableFolderId,
  handleKeyDown,
  getFocusableTabIndex,
  isFirstItem = false,
  newFolder,
  destinationDisables,
  sessionCreatedFolderIds,
  onDeleteSessionFolder,
  deleteSessionFolderAriaLabel,
  isDeletingSessionFolder,
}: FolderTreeBranchProps) => {
  const folderId = String(folder.id);
  const depthDisabledFolderIds = destinationDisables?.depthDisabledFolderIds;
  const depthDisabledTooltip = destinationDisables?.depthDisabledTooltip;
  const selectionDisabledFolderIds =
    destinationDisables?.selectionDisabledFolderIds;
  const pendingNewFolder = newFolder?.pending;
  const isDepthDisabled = depthDisabledFolderIds?.has(folderId) ?? false;
  const selectionDisabledTooltip =
    selectionDisabledFolderIds?.get(folderId) ?? undefined;
  const isSelectionDisabled = selectionDisabledTooltip !== undefined;
  const effectiveDisabled =
    !!disabled || isDepthDisabled || isSelectionDisabled;
  const isFolderExpanded = expandedFolders.has(folderId);
  const hasChildren = !!folder?.children?.length;
  const shouldShowPendingInput =
    !!newFolder && pendingNewFolder?.parentId === folderId;
  const childGroupId =
    hasChildren || shouldShowPendingInput
      ? `folder-${folder.id}-children`
      : undefined;

  // Determine tabIndex using roving tabindex pattern
  // First, try to use the hook's getFocusableTabIndex
  // Fallback: if nothing is selected and this is the first root item, make it tabbable
  let tabIndex = getFocusableTabIndex
    ? getFocusableTabIndex(folderId)
    : selectedFolderId === folderId
      ? 0
      : !selectedFolderId && folderLevel === 0 && isFirstItem
        ? 0
        : -1;

  // Override: ensure first root-level item is tabbable when nothing else is
  if (
    tabIndex === -1 &&
    !selectedFolderId &&
    folderLevel === 0 &&
    isFirstItem
  ) {
    tabIndex = 0;
  }

  const handleExpand = () => {
    if (onToggleExpanded) {
      onToggleExpanded(folderId);
    }
  };

  const handleNodeKeyDown = (event: KeyboardEvent) => {
    if (handleKeyDown) {
      handleKeyDown(event, folder);
    }
  };

  const sharedBranchProps = {
    onItemClick,
    searchQuery,
    selectedFolderId,
    expandedFolders,
    onToggleExpanded,
    focusableFolderId,
    handleKeyDown,
    getFocusableTabIndex,
    newFolder,
    destinationDisables,
    sessionCreatedFolderIds,
    onDeleteSessionFolder,
    deleteSessionFolderAriaLabel,
    isDeletingSessionFolder,
  };

  const nodeDisabledTooltip =
    selectionDisabledTooltip ??
    (isDepthDisabled ? depthDisabledTooltip : undefined);
  const showDelete = sessionCreatedFolderIds?.has(folderId) ?? false;

  return (
    <li role="none" data-folder-id={folderId}>
      <FolderTreeNode
        folder={folder}
        folderLevel={folderLevel}
        isFolderExpanded={isFolderExpanded}
        onExpand={handleExpand}
        onItemClick={onItemClick}
        searchQuery={searchQuery}
        selectedFolderId={selectedFolderId}
        disabled={effectiveDisabled}
        tabIndex={tabIndex}
        onKeyDown={handleNodeKeyDown}
        ariaOwns={childGroupId}
        disabledTooltip={nodeDisabledTooltip}
        showDelete={showDelete}
        onDelete={onDeleteSessionFolder}
        deleteAriaLabel={deleteSessionFolderAriaLabel}
        isDeleting={isDeletingSessionFolder}
      />
      {/* Recursively render children in a group */}
      {isFolderExpanded &&
        !disabled &&
        (hasChildren || shouldShowPendingInput) && (
          <ul role="group" id={childGroupId} className={styles.folderTreeGroup}>
            {hasChildren
              ? folder.children!.map((child) => (
                  <FolderTreeBranch
                    folder={child as FolderTreeItem}
                    folderLevel={folderLevel + 1}
                    key={child.id}
                    {...sharedBranchProps}
                  />
                ))
              : undefined}
            {shouldShowPendingInput && newFolder ? (
              <NewFolderRow
                folderLevel={folderLevel + 1}
                onCommit={newFolder.onCommit}
                onCancel={newFolder.onCancel}
                placeholder={newFolder.placeholder}
                draftName={pendingNewFolder?.draftName}
                inputAriaLabel={newFolder.inputAriaLabel}
                isCreating={newFolder.isCreating}
              />
            ) : undefined}
          </ul>
        )}
    </li>
  );
};

// Rows re-render on any tree-level change; most are unaffected by a selection
// move or a keystroke, so let them bail out.
export const FolderTreeBranch = memo(FolderTreeBranchComponent);

export default FolderTreeBranch;
